import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import { Provider } from 'react-redux'
import { createStore } from '../store/store'
import { ThemeProvider, createTheme } from '../pixel/createTheme'
import { LocalizationProvider } from '../context/Localization/LocalizationProvider'
import { LocalizationValues } from '../context/Localization/LocalizationTypes'
import { ViewModeProvider } from '../context/ViewMode/ViewModeProvider'

import CriteriaEngine from './CriteriaEngine'
import { CustomFieldsProvider } from '../context/CustomField/CustomFieldProvider'
import { transformGroupConditionToPayload, transformPayloadToGroupCondition } from '../util/groupConditionTransform'
import ErrorBoundary from '../../../components/ErrorBoundary'
import Dialog from '../pixel/Dialog'
import { isEqual } from '../util/groupConditionCompare'

export const App = (props: any) => {
    const theme = useMemo(createTheme, [])

    const [confirmationOpen, setConfirmationOpen] = useState(false)
    const [newTabName, setNewTabName] = useState('/offerForm')

    const originalConditionData = useMemo(() => transformPayloadToGroupCondition(props.offerService.getConditionData()), [])
    const savedConditionData = useMemo(() => transformPayloadToGroupCondition(props.offerService.getConditionData()), [])

    const store = useMemo(() => createStore({ 
        groupConditions: {
            groupConditions: savedConditionData,
            validated: false,
        } 
    }), [])

    const handleAllowChangeHandler = (newTabName: string) => {
        if (isEqual(props.offerService.getConditionData(), transformGroupConditionToPayload(store.getState().groupConditions.groupConditions))) {
            return false
        } else {
            setConfirmationOpen(true)
            setNewTabName(newTabName)
            return true
        }
    };

    if (typeof props.setupTabHandler === 'function') {
        props.setupTabHandler(handleAllowChangeHandler);
    }

    return (
        <React.StrictMode>
            <ErrorBoundary text="Something went wrong">
                <Provider store={store}>
                    <LocalizationProvider locale='en-us' translations={props.translations as LocalizationValues}>
                        <ThemeProvider theme={theme}>
                            <CustomFieldsProvider offerService={props.offerService}>
                                <ViewModeProvider readOnly={props.offerService.offerFormData.isDefault && !props.isCloneForm} >
                                    <CriteriaEngine
                                        offerService={props.offerService}
                                        offerFormEId={props.offerService.offerFormData.eid || ''}
                                        onBack={props.onBack}
                                        onNext={props.onNext}
                                        onCancelForm={props.onCancelForm}
                                        isEditForm={props.isEditForm}
                                    />
                                    <Dialog
                                        open={confirmationOpen}
                                        resolveActions={[{ label: props.translations.CONFIRM }]}
                                        rejectActions={[{ label: props.translations.CANCEL }]}
                                        title={props.translations.CANCELOFFERFORM}
                                        description={props.translations.UNSAVEDCHANGES}
                                        onResolve={() => {
                                            setConfirmationOpen(false)
                                            props.offerService.saveConditionData(transformGroupConditionToPayload(originalConditionData));
                                            props.changeTab(newTabName, true)
                                        }}
                                        onReject={() => setConfirmationOpen(false)}
                                        onClose={() => { }}
                                        onExited={() => { }}
                                    />
                                </ViewModeProvider>
                            </CustomFieldsProvider>
                        </ThemeProvider>
                    </LocalizationProvider>
                </Provider>
            </ErrorBoundary>
        </React.StrictMode>
    )
}

const InjectedAngularPropTypes = PropTypes.shape({
    '$http': PropTypes.func,
    'offerService': PropTypes.object,
    locale: PropTypes.string,
    onBack: PropTypes.func,
    onNext: PropTypes.func,
    onCancelForm: PropTypes.func,
    getConditionData: PropTypes.func,
    translations: PropTypes.object,
    isCloneForm: PropTypes.bool.isRequired,
    isEditForm: PropTypes.bool.isRequired,
    setupTabHandler: PropTypes.func.isRequired,
    changeTab: PropTypes.func.isRequired,
}).isRequired

App.propTypes = InjectedAngularPropTypes

export { react2angular } from '../lib/react2Angular'

export default App